import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight, Check, Copy, Lock } from "lucide-react";

type Row = { home: string; away: string; tip: string; locked?: boolean };

const ROWS: Row[] = [
  { home: "SVK", away: "CZE", tip: "1:2" },
  { home: "GER", away: "ESP", tip: "0:0", locked: true },
  { home: "FRA", away: "ITA", tip: "2:1" },
];

// Stages: 0 idle, 1 copying, 2 saving, 3 done
export function BulkTipsDemo() {
  const { t } = useTranslation();
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const seq = [1200, 1100, 1400, 1800]; // ms per stage before advancing
    const id = setTimeout(() => setStage((s) => (s + 1) % 4), seq[stage]);
    return () => clearTimeout(id);
  }, [stage]);

  return (
    <div className="mx-auto mt-10 max-w-3xl rounded-2xl border border-pitch bg-white/[0.02] p-4 sm:p-6 backdrop-blur">
      <div className="mb-4 flex items-center justify-between text-xs uppercase tracking-widest text-pitch-cream/60">
        <span>{t("landing.bulkDemo.caption")}</span>
        <span className="inline-flex items-center gap-1 text-primary">
          <Copy className="h-3.5 w-3.5" /> demo
        </span>
      </div>

      <div className="grid gap-4 md:grid-cols-[1fr_auto_1fr] md:items-stretch">
        {/* Source league */}
        <LeagueCard
          name={t("landing.bulkDemo.source")}
          rows={ROWS}
          tone="source"
          stage={stage}
        />

        {/* Arrow / copy indicator */}
        <div className="flex md:flex-col items-center justify-center gap-2 text-primary">
          <div
            className={`rounded-full border border-primary/40 bg-primary/10 p-2 transition-all duration-500 ${
              stage >= 1 ? "scale-110 shadow-[0_0_24px_hsl(var(--primary)/0.45)]" : "opacity-60"
            }`}
            aria-hidden
          >
            <ArrowRight className="h-4 w-4 md:rotate-0 rotate-90 sm:rotate-0" />
          </div>
          <span className="text-[10px] uppercase tracking-widest text-pitch-cream/50">
            {t("landing.bulkDemo.copy")}
          </span>
        </div>

        {/* Target league */}
        <LeagueCard
          name={t("landing.bulkDemo.target")}
          rows={ROWS}
          tone="target"
          stage={stage}
        />
      </div>

      {/* Status line */}
      <div className="mt-4 flex items-center justify-center gap-2 text-sm">
        {stage < 2 && (
          <span className="text-pitch-cream/60">{t("landing.bulkDemo.statusIdle")}</span>
        )}
        {stage === 2 && (
          <span className="text-pitch-cream/80">
            <span className="inline-block animate-pulse">●</span> {t("landing.bulkDemo.statusSaving")}
          </span>
        )}
        {stage === 3 && (
          <span className="text-pitch-cream/80">
            <Check className="inline h-4 w-4 text-primary" />{" "}
            <span>
              {t("landing.bulkDemo.statusDone", { saved: 2, skipped: 1 })}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}

function LeagueCard({
  name,
  rows,
  tone,
  stage,
}: {
  name: string;
  rows: Row[];
  tone: "source" | "target";
  stage: number;
}) {
  return (
    <div className="rounded-xl border border-pitch bg-pitch-night/40 p-3">
      <div className="mb-2 flex items-center justify-between">
        <span className="text-xs font-semibold text-pitch-cream/80">{name}</span>
        <span className="text-[10px] uppercase tracking-widest text-pitch-cream/40">
          {tone === "source" ? "A" : "B"}
        </span>
      </div>
      <ul className="space-y-1.5">
        {rows.map((r, i) => {
          const showTip = tone === "source" || stage >= 1;
          const saved = tone === "target" && stage >= 3 && !r.locked;
          const rejected = tone === "target" && stage >= 3 && r.locked;
          return (
            <li
              key={`${r.home}-${r.away}`}
              className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm transition-all duration-500 ${
                rejected
                  ? "border-destructive/40 bg-destructive/10"
                  : saved
                  ? "border-primary/40 bg-primary/10"
                  : "border-pitch bg-white/[0.02]"
              }`}
              style={{ transitionDelay: `${i * 120}ms` }}
            >
              <span className="text-pitch-cream/80">
                {r.home} <span className="text-pitch-cream/40">vs</span> {r.away}
              </span>
              <span className="flex items-center gap-2">
                {r.locked && tone === "target" && (
                  <Lock className="h-3.5 w-3.5 text-destructive" aria-label="locked" />
                )}
                <span
                  className={`font-mono tabular-nums ${
                    rejected ? "text-destructive/80 line-through" : "text-pitch-cream"
                  } ${!showTip ? "opacity-0" : "opacity-100"} transition-opacity duration-500`}
                >
                  {r.tip}
                </span>
                {saved && <Check className="h-3.5 w-3.5 text-primary" />}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
